/**
 * Shared media capture utilities
 *
 * Extracted from useCamera and useScreenShare to eliminate ~60% overlap in:
 * - Video-element → canvas → blob/base64 pipeline
 * - Frame dimension clamping
 * - Frame quality validation (brightness, contrast, blur)
 * - Metrics tracking
 * - Tab-visibility / page-hide cleanup registration
 * - uploadMediaForAnalysis + sendContextUpdate pattern
 */

import { logger } from '../../lib/logger-client.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CaptureMetrics {
  captureCount: number
  failedCaptures: number
  avgCaptureTime: number
}

export interface CaptureResult {
  blob: Blob
  imageData: string
  width: number
  height: number
  timestamp: number
}

export type MediaModality = 'webcam' | 'screen'

// ---------------------------------------------------------------------------
// Canvas capture pipeline (video element → scaled canvas → blob + dataURL)
// ---------------------------------------------------------------------------

/**
 * Capture one frame from a video element into a canvas.
 * Returns null if the video is not ready or dimensions are zero.
 */
export async function captureVideoFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  options: { maxDimension?: number; quality?: number } = {}
): Promise<CaptureResult | null> {
  const { maxDimension = 1280, quality = 0.7 } = options

  if (video.readyState < 2) {
    logger.debug('[capture-utils] Video not ready for capture', { readyState: video.readyState })
    return null
  }

  const videoWidth = video.videoWidth
  const videoHeight = video.videoHeight

  if (videoWidth === 0 || videoHeight === 0) {
    logger.warn('[capture-utils] Video dimensions are zero')
    return null
  }

  let width = videoWidth
  let height = videoHeight

  if (maxDimension && (width > maxDimension || height > maxDimension)) {
    const scale = maxDimension / Math.max(width, height)
    width = Math.round(width * scale)
    height = Math.round(height * scale)
  }

  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: true })
  if (!ctx) throw new Error('[capture-utils] Failed to get canvas context')

  ctx.drawImage(video, 0, 0, width, height)

  const blob = await new Promise<Blob | null>(resolve => {
    canvas.toBlob(resolve, 'image/jpeg', quality)
  })

  if (!blob) throw new Error('[capture-utils] Failed to create blob from canvas')

  // Derive data URL from the existing blob instead of re-encoding via toDataURL
  const imageData = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('[capture-utils] FileReader failed'))
    reader.readAsDataURL(blob)
  })

  return { blob, imageData, width, height, timestamp: Date.now() }
}

// ---------------------------------------------------------------------------
// Frame quality validation (brightness, contrast, Laplacian blur)
// ---------------------------------------------------------------------------

/**
 * Validates frame quality directly from a canvas that already has the frame drawn.
 * Returns a score in [0, 1] where 1 = best quality.
 * Runs synchronously — no encode/decode roundtrip.
 * Falls back to 0.5 if the canvas context is unavailable.
 */
export function validateFrameQuality(canvas: HTMLCanvasElement): number {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return 0.5

  const w = canvas.width
  const h = canvas.height
  if (w === 0 || h === 0) return 0.5

  const data = ctx.getImageData(0, 0, w, h).data
  const pixelCount = data.length / 4

  // Brightness: average of all pixels; optimal midpoint is 128
  // Loop bounds guarantee valid indices; non-null assertions are zero-cost at runtime
  let brightnessSum = 0
  for (let i = 0; i < data.length; i += 4) {
    brightnessSum += (data[i]! + data[i + 1]! + data[i + 2]!) / 3
  }
  const avgBrightness = brightnessSum / pixelCount
  const brightnessScore = 1 - Math.abs(avgBrightness - 128) / 128

  // Contrast: standard deviation of brightness
  let variance = 0
  for (let i = 0; i < data.length; i += 4) {
    const b = (data[i]! + data[i + 1]! + data[i + 2]!) / 3
    variance += (b - avgBrightness) * (b - avgBrightness)
  }
  const stdDev = Math.sqrt(variance / pixelCount)
  const contrastScore = Math.min(stdDev / 50, 1)

  // Blur: Laplacian variance (higher = sharper)
  let laplacianSum = 0
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = (y * w + x) * 4
      const center = (data[idx]! + data[idx + 1]! + data[idx + 2]!) / 3
      const right = (data[idx + 4]! + data[idx + 5]! + data[idx + 6]!) / 3
      const bIdx = idx + w * 4
      const bottom = (data[bIdx]! + data[bIdx + 1]! + data[bIdx + 2]!) / 3
      laplacianSum += Math.abs(center * 2 - right - bottom)
    }
  }
  const laplacianVariance = laplacianSum / ((w - 2) * (h - 2))
  const blurScore = Math.min(laplacianVariance / 100, 1)

  return brightnessScore * 0.3 + contrastScore * 0.4 + blurScore * 0.3
}

// ---------------------------------------------------------------------------
// Metrics helpers
// ---------------------------------------------------------------------------

export function createMetrics(): CaptureMetrics & { lastAnalysis?: string } {
  return { captureCount: 0, failedCaptures: 0, avgCaptureTime: 0 }
}

export function recordCaptureTime(metrics: CaptureMetrics, elapsedMs: number): void {
  metrics.captureCount++
  metrics.avgCaptureTime =
    (metrics.avgCaptureTime * (metrics.captureCount - 1) + elapsedMs) / metrics.captureCount
}

export function getMetricsSnapshot(metrics: CaptureMetrics): CaptureMetrics {
  return { ...metrics, avgCaptureTime: Math.round(metrics.avgCaptureTime * 10) / 10 }
}

/**
 * Wait until a video element has decoded enough data to capture frames.
 * Resolves with the element when readyState >= 2 and dimensions are non-zero.
 */
export function waitForVideoReady(
  getVideo: () => HTMLVideoElement | null,
  options: { timeoutMs?: number } = {}
): Promise<HTMLVideoElement | null> {
  const timeoutMs = options.timeoutMs ?? 5000

  return new Promise(resolve => {
    const startedAt = Date.now()
    let settled = false
    let cleanup: (() => void) | undefined

    const finish = (video: HTMLVideoElement | null) => {
      if (settled) return
      settled = true
      cleanup?.()
      resolve(video)
    }

    const isReady = (video: HTMLVideoElement | null): video is HTMLVideoElement =>
      Boolean(video && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0)

    const poll = () => {
      const video = getVideo()
      if (isReady(video)) {
        finish(video)
        return
      }
      if (Date.now() - startedAt >= timeoutMs) {
        finish(null)
        return
      }
      window.requestAnimationFrame(poll)
    }

    const video = getVideo()
    if (video) {
      const onReady = () => {
        if (isReady(getVideo())) {
          finish(getVideo())
        }
      }
      video.addEventListener('loadeddata', onReady)
      video.addEventListener('canplay', onReady)
      cleanup = () => {
        video.removeEventListener('loadeddata', onReady)
        video.removeEventListener('canplay', onReady)
      }
      if (isReady(video)) {
        finish(video)
        return
      }
    }

    window.requestAnimationFrame(poll)
  })
}

// ---------------------------------------------------------------------------
// Track stop + cleanup helpers
// ---------------------------------------------------------------------------

/** Stop all tracks on a stream and clear the ref. */
export function stopStreamTracks(stream: MediaStream): void {
  stream.getTracks().forEach(track => {
    const t = track as MediaStreamTrack & { _fbcEndedHandler?: () => void }
    if (t._fbcEndedHandler) {
      track.removeEventListener('ended', t._fbcEndedHandler)
      delete t._fbcEndedHandler
    }
    track.stop()
  })
}

// ---------------------------------------------------------------------------
// Tab-visibility / page-hide cleanup registration
// ---------------------------------------------------------------------------

export interface VisibilityCleanupOptions {
  label: string
  getStream: () => MediaStream | null
  clearStream: () => void
  onStop: () => void
}

/**
 * Registers `visibilitychange` and `pagehide` listeners that stop the stream
 * when the tab is hidden or the page is unloading.
 *
 * Returns an unregister function to be called from the hook's cleanup effect.
 */
export function registerVisibilityCleanup(opts: VisibilityCleanupOptions): () => void {
  if (typeof document === 'undefined') return () => {}

  const handleVisibilityChange = () => {
    if (document.hidden && opts.getStream()) {
      logger.info(`${opts.label} Tab hidden — stopping media to prevent memory leak`)
      opts.onStop()
    }
  }

  const handlePageHide = () => {
    const stream = opts.getStream()
    if (stream) {
      logger.info(`${opts.label} Page hiding — force cleanup`)
      stopStreamTracks(stream)
      opts.clearStream()
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pagehide', handlePageHide)

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('pagehide', handlePageHide)
  }
}

// ---------------------------------------------------------------------------
// Analysis + context update pipeline
// ---------------------------------------------------------------------------

export interface AnalysisUploadOptions {
  blob: Blob
  imageData: string
  sessionId: string
  modality: MediaModality
  voiceConnectionId?: string | undefined
  lastAnalysisAtRef: React.MutableRefObject<number>
  analysisIntervalMs: number
  onAnalysis?: ((analysis: string, imageData: string, capturedAt: number) => void) | undefined
  sendContextUpdate?:
    | ((update: {
        sessionId?: string | null | undefined
        modality: MediaModality
        analysis: string
        imageData?: string | undefined
        capturedAt?: number | undefined
        metadata?: Record<string, unknown> | undefined
      }) => void)
    | undefined
  capturedAt: number
  extraMetadata?: Record<string, unknown> | undefined
}

/**
 * Conditionally uploads media for analysis (rate-limited by analysisIntervalMs)
 * and fires onAnalysis + sendContextUpdate when a result arrives.
 *
 * Returns the analysis string if one was produced, otherwise null.
 */
export async function maybeAnalyzeAndBroadcast(
  opts: AnalysisUploadOptions
): Promise<string | null> {
  const now = Date.now()
  if (now - opts.lastAnalysisAtRef.current < opts.analysisIntervalMs) return null

  // Optimistically set timestamp BEFORE the await to prevent concurrent calls
  // from passing the interval check while the first upload is still in-flight.
  opts.lastAnalysisAtRef.current = now

  try {
    const { uploadMediaForAnalysis } = await import('./upload-helpers.js')
    const result = await uploadMediaForAnalysis({
      blob: opts.blob,
      imageData: opts.imageData,
      sessionId: opts.sessionId,
      modality: opts.modality,
      ...(opts.voiceConnectionId !== undefined && { voiceConnectionId: opts.voiceConnectionId }),
    })

    if (!result?.analysis) return null

    opts.onAnalysis?.(result.analysis, opts.imageData, opts.capturedAt)

    opts.sendContextUpdate?.({
      sessionId: opts.sessionId ?? null,
      modality: opts.modality,
      analysis: result.analysis,
      imageData: opts.imageData,
      capturedAt: opts.capturedAt,
      metadata: opts.extraMetadata,
    })

    return result.analysis
  } catch (err) {
    logger.warn('[capture-utils] maybeAnalyzeAndBroadcast failed', { error: err })
    return null
  }
}
